import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { reverseStrength } from "domains/assets/utils";
import { useDebounce } from "domains/commons/hooks/useDebounce";
import useGetImageDimensions from "domains/commons/hooks/useGetImageDimensions";
import { makeNumberAMultipleOfEight } from "domains/commons/misc";
import {
  getImageDimensions,
  getImagesRatioDiff,
} from "domains/commons/utils/getImageDimensions";
import {
  BaseModelId,
  SDXL_BASE_MODELS,
  SDXL_DEFAULT_BASE_MODEL,
} from "domains/inference/constants/baseModel";
import { isInferenceType } from "domains/inference/constants/inferenceTypes";
import {
  GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE,
  getGuidanceForModel,
  getMaxNbImagesForModel,
  getSamplingStepsForModel,
  IMAGE_FOR_DIMENSIONS_BY_INFERENCE_TYPE,
} from "domains/inference/constants/pipelinesConfigs";
import { SCHEDULER_TYPE } from "domains/inference/constants/Schedulers";
import { useSectionsContext } from "domains/inference/contexts/SectionsProvider";
import {
  ControlNetPreset,
  getAllowedControlNetPreset,
  getControlNetInfluenceFromString,
  getControlNetPresetFromString,
} from "domains/inference/enum/ControlNetPreset";
import { ReferenceAsset } from "domains/inference/interfaces/InferenceGenerator";
import {
  getDimensionLimits,
  getDimensionValues,
  getIsCustomDimensions,
} from "domains/inference/utils/dimensionValues";
import useLastModelId from "domains/models/hooks/useLastModelId";
import {
  getIsModelFlux,
  getIsModelFluxPro,
  getIsModelFluxPro1_1,
  getIsModelFluxPro1_1Ultra,
  getIsModelGptImage,
  getIsModelSdxl,
  getIsModelWithBase,
} from "domains/models/utils";
import useRouter from "domains/navigation/hooks/useRouter";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelPresetsByModelIdApiResponse,
  GetModelsByModelIdApiArg,
  GetModelsByModelIdApiResponse,
  GetModelsInferencesByModelIdAndInferenceIdApiResponse,
  PostModelsInferencesByModelIdApiArg,
  useLazyGetAssetsByAssetIdQuery,
  useLazyGetModelsByModelIdQuery,
} from "infra/api/generated/api";
import { isEqual } from "lodash";
import _ from "lodash";

export interface InferenceCreateContextValue {
  isAbleToGenerate: boolean;
  isImportingAsset: boolean;
  setIsImportingAsset: (value: boolean) => void;

  model?: GetModelsByModelIdApiResponse["model"];
  isLoadingModel: boolean;
  setModel: (
    model: GetModelsByModelIdApiResponse["model"],
    options?: {
      avoidDimensionChange?: boolean;
    }
  ) => void;
  setModelFromModelId: (
    modelId: GetModelsByModelIdApiArg["modelId"],
    concepts?: GetModelsByModelIdApiResponse["model"]["concepts"]
  ) => void;
  baseModelId: string;
  setBaseModelId: (baseModelId: string) => void;

  nbImages: number;
  setNbImages: (images: number) => void;
  maxNbImages: number;
  samplingSteps: number;
  setSamplingSteps: (samplingSteps: number) => void;
  samplingStepsMin: number;
  setSamplingStepsMin: (samplingStepsMin: number) => void;
  samplingStepsMax: number;
  setSamplingStepsMax: (samplingStepsMax: number) => void;

  isSdxl: boolean;
  isModelWithBase: boolean;
  isFlux: boolean;
  isFluxPro1_1: boolean;
  isGptImage: boolean;

  concepts: GetModelsByModelIdApiResponse["model"]["concepts"] | undefined;
  setConcepts: React.Dispatch<
    React.SetStateAction<
      GetModelsByModelIdApiResponse["model"]["concepts"] | undefined
    >
  >;

  prompt: string;
  setPrompt: (prompt: string) => void;
  negativePrompt: string;
  setNegativePrompt: (prompt: string) => void;

  referenceImages: {
    reference: ReferenceAsset | undefined;
    control: ReferenceAsset | undefined;
    ipAdapter: ReferenceAsset | undefined;
  };
  isReferencesRatioValid: boolean;
  setReferenceImage: (
    type: "reference" | "control" | "ipAdapter",
    asset: ReferenceAsset | undefined,
    options?: { preventDimensionsUpdate?: boolean }
  ) => void;
  setReferenceImageFromAssetId: (
    type: "reference" | "control" | "ipAdapter",
    assetId: string,
    options?: { preventDimensionsUpdate?: boolean }
  ) => Promise<void>;
  referenceInfluences: {
    reference: number;
    control: number;
    ipAdapter: number;
  };
  setReferenceInfluence: (
    type: "reference" | "control" | "ipAdapter",
    strength: number
  ) => void;
  inferenceType: PostModelsInferencesByModelIdApiArg["body"]["parameters"]["type"];
  setInferenceType: (
    inferenceType: PostModelsInferencesByModelIdApiArg["body"]["parameters"]["type"]
  ) => void;
  referenceControlNet: ControlNetPreset;
  setReferenceControlNet: (referenceControlNet: ControlNetPreset) => void;
  isReferenceModeMappingEnabled: boolean;
  setIsReferenceModeMappingEnabled: (
    isReferenceModeMappingEnabled: boolean
  ) => void;
  controlEnd: number;
  setControlEnd: (controlEnd: number) => void;
  referenceModeMapAsset: GetAssetsByAssetIdApiResponse["asset"] | undefined;
  setReferenceModeMapAsset: (
    asset: GetAssetsByAssetIdApiResponse["asset"] | undefined
  ) => void;
  isReferenceModeMapLoading: boolean;
  setIsReferenceModeMapLoading: (value: boolean) => void;
  referenceAttn: boolean;
  setReferenceAttn: (value: boolean) => void;
  referenceAdain: boolean;
  setReferenceAdain: (value: boolean) => void;
  ipAdapterType: NonNullable<
    PostModelsInferencesByModelIdApiArg["body"]["parameters"]["ipAdapterType"]
  >;
  setIpAdapterType: (
    ipAdapterType: NonNullable<
      PostModelsInferencesByModelIdApiArg["body"]["parameters"]["ipAdapterType"]
    >
  ) => void;

  width: number;
  minWidth: number;
  maxWidth: number;
  setWidth: (width: number) => number;
  height: number;
  minHeight: number;
  maxHeight: number;
  setHeight: (height: number) => number;
  guidance: number;
  setGuidance: (guidance: number) => void;
  guidanceMin: number;
  guidanceMax: number;
  seed: string;
  setSeed: (seed: string) => void;
  scheduler: SCHEDULER_TYPE | undefined;
  setScheduler: (scheduler: SCHEDULER_TYPE | undefined) => void;

  reset: () => void;
  mask?: string;
  setMask: (mask: string) => void;

  cuCost: number | undefined;
  setCuCost: (cuCost: number | undefined) => void;
  isCuLoading: boolean;
  setIsCuLoading: (isCuLoading: boolean) => void;

  isInCanvas: boolean;
  isTexture: boolean;
  canHaveReference: boolean;

  setIsCanvasLiveMode: (value: boolean) => void;
  isCanvasLiveMode: boolean;
  setReferenceFromInference: (
    inference:
      | GetModelsInferencesByModelIdAndInferenceIdApiResponse["inference"]
      | GetModelPresetsByModelIdApiResponse["presets"][0]
  ) => void;
}

export const InferenceCreateContext =
  createContext<InferenceCreateContextValue>({
    isAbleToGenerate: false,
    isImportingAsset: false,
    setIsImportingAsset: () => {},

    model: undefined,
    isLoadingModel: false,
    setModel: () => {},
    setModelFromModelId: () => {},
    baseModelId: SDXL_DEFAULT_BASE_MODEL.id,
    setBaseModelId: () => {},

    nbImages: 0,
    setNbImages: () => {},
    maxNbImages: 16,
    samplingSteps: 0,
    setSamplingSteps: () => {},
    samplingStepsMin: 10,
    setSamplingStepsMin: () => {},
    samplingStepsMax: 150,
    setSamplingStepsMax: () => {},
    referenceImages: {
      reference: undefined,
      control: undefined,
      ipAdapter: undefined,
    },
    isReferencesRatioValid: true,
    setReferenceImage: () => {},
    setReferenceImageFromAssetId: async () => {},
    width: 0,
    minWidth: 0,
    maxWidth: 0,
    setWidth: () => 0,
    height: 0,
    minHeight: 0,
    maxHeight: 0,
    setHeight: () => 0,
    guidance: 0,
    setGuidance: () => {},
    guidanceMin: 0,
    guidanceMax: 0,
    seed: "",
    setSeed: () => {},
    referenceInfluences: {
      reference: 0,
      control: 0,
      ipAdapter: 0,
    },
    setReferenceInfluence: () => {},
    inferenceType: "img2img",
    setInferenceType: () => {},
    referenceControlNet: ControlNetPreset.DEPTH,
    setReferenceControlNet: () => {},
    prompt: "",
    setPrompt: () => {},
    negativePrompt: "",
    setNegativePrompt: () => {},
    isReferenceModeMappingEnabled: true,
    setIsReferenceModeMappingEnabled: () => {},
    controlEnd: 0,
    setControlEnd: () => {},
    scheduler: undefined,
    setScheduler: () => {},
    reset: () => {},
    mask: undefined,
    setMask: () => {},
    isSdxl: false,
    isModelWithBase: false,
    isFlux: false,
    isFluxPro1_1: false,
    isGptImage: false,
    referenceModeMapAsset: undefined,
    setReferenceModeMapAsset: () => {},
    isReferenceModeMapLoading: false,
    setIsReferenceModeMapLoading: () => {},
    referenceAttn: true,
    setReferenceAttn: () => {},
    referenceAdain: false,
    setReferenceAdain: () => {},
    ipAdapterType: "character",
    setIpAdapterType: () => {},
    concepts: undefined,
    setConcepts: () => {},
    cuCost: undefined,
    setCuCost: () => {},
    isCuLoading: false,
    setIsCuLoading: () => {},
    isInCanvas: false,
    isTexture: false,
    canHaveReference: false,
    setIsCanvasLiveMode: () => {},
    isCanvasLiveMode: false,
    setReferenceFromInference: () => {},
  });

// ------------------------------------

interface InferenceCreateProviderProps extends PropsWithChildren {
  isInCanvas?: boolean;
  isTexture?: boolean;
  defaultControlNetPreset?: ControlNetPreset;
}

export function InferenceCreateProvider({
  isInCanvas = false,
  isTexture = false,
  defaultControlNetPreset,
  children,
}: InferenceCreateProviderProps) {
  const router = useRouter();
  const { maxBatchStandard, subscription } = usePlanContext();
  const { infoToast } = useScenarioToast();
  const { selectedTeam, selectedProject } = useTeamContext();
  const { toggleCollapsedSection } = useSectionsContext();
  const [getAssetById] = useLazyGetAssetsByAssetIdQuery();
  const [getModelById] = useLazyGetModelsByModelIdQuery();
  const [model, __setModel] = useState<
    GetModelsByModelIdApiResponse["model"] | undefined
  >();
  const [baseModelId, _setBaseModelId] = useState<BaseModelId>(
    SDXL_DEFAULT_BASE_MODEL.id
  );
  const [isImportingAsset, setIsImportingAsset] = useState<boolean>(false);
  const [isLoadingModel, setIsLoadingModel] = useState<boolean>(false);
  const [nbImages, __setNbImages] = useState<number>(
    selectedTeam.isFreePlan ? 2 : 4
  );
  const [samplingSteps, _setSamplingSteps] = useState<number>(
    getSamplingStepsForModel({
      model: undefined,
      scheduler: undefined,
    }).default
  );
  const [samplingStepsMin, setSamplingStepsMin] = useState<number>(
    getSamplingStepsForModel({
      model: undefined,
      scheduler: undefined,
    }).min
  );
  const [samplingStepsMax, setSamplingStepsMax] = useState<number>(
    getSamplingStepsForModel({
      model: undefined,
      scheduler: undefined,
    }).max
  );
  const [referenceImages, setReferenceImages] = useState<{
    reference: ReferenceAsset | undefined;
    control: ReferenceAsset | undefined;
    ipAdapter: ReferenceAsset | undefined;
  }>({
    reference: undefined,
    control: undefined,
    ipAdapter: undefined,
  });
  const [width, _setWidth] = useState<number>(512);
  const [height, _setHeight] = useState<number>(512);
  const [guidance, _setGuidance] = useState<number>(
    getGuidanceForModel({
      model: undefined,
      scheduler: undefined,
    }).default
  );
  const [guidanceMin, setGuidanceMin] = useState<number>(
    getGuidanceForModel({
      model: undefined,
      scheduler: undefined,
    }).min
  );
  const [guidanceMax, setGuidanceMax] = useState<number>(
    getGuidanceForModel({
      model: undefined,
      scheduler: undefined,
    }).max
  );
  const [seed, setSeed] = useState<string>("");
  const [referenceInfluences, setReferenceInfluences] = useState<{
    reference: number;
    control: number;
    ipAdapter: number;
  }>({
    reference: GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE.img2img(
      undefined,
      false
    ).reference,
    control: GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE.img2img(undefined, false)
      .control,
    ipAdapter: GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE.img2img(
      undefined,
      false
    ).ipAdapter,
  });
  const [inferenceType, __setInferenceType] =
    useState<PostModelsInferencesByModelIdApiArg["body"]["parameters"]["type"]>(
      "img2img"
    );
  const [referenceControlNet, _setReferenceControlNet] =
    useState<ControlNetPreset>(
      defaultControlNetPreset ?? ControlNetPreset.DEPTH
    );
  const [prompt, setPrompt] = useState<string>("");
  const [negativePrompt, setNegativePrompt] = useState<string>("");
  const [isReferenceModeMappingEnabled, setIsReferenceModeMappingEnabled] =
    useState<boolean>(true);
  const [controlEnd, setControlEnd] = useState<number>(0.6);
  const [mask, setMask] = useState<string | undefined>();
  const [scheduler, __setScheduler] = useState<SCHEDULER_TYPE | undefined>();
  const [referenceModeMapAsset, setReferenceModeMapAsset] = useState<
    GetAssetsByAssetIdApiResponse["asset"] | undefined
  >();
  const [isReferenceModeMapLoading, setIsReferenceModeMapLoading] =
    useState<boolean>(false);
  const [referenceAttn, setReferenceAttn] = useState<boolean>(true);
  const [referenceAdain, setReferenceAdain] = useState<boolean>(false);
  const [ipAdapterType, setIpAdapterType] =
    useState<
      NonNullable<
        PostModelsInferencesByModelIdApiArg["body"]["parameters"]["ipAdapterType"]
      >
    >("character");
  const [concepts, setConcepts] = useState<
    GetModelsByModelIdApiResponse["model"]["concepts"] | undefined
  >();
  const [isCanvasLiveMode, setIsCanvasLiveMode] = useState<boolean>(false);
  const [cuCost, setCuCost] = useState<number | undefined>(undefined);
  const [isCuLoading, setIsCuLoading] = useState<boolean>(false);
  const [, setLastModelId] = useLastModelId(isTexture ? "texture" : "image");

  const planSamplingStepsLimit =
    subscription?.plan === "free" || subscription?.plan === "cu-creator"
      ? 30
      : undefined;

  const isSdxl = useMemo(() => getIsModelSdxl(model), [model]);
  const isFlux = useMemo(() => getIsModelFlux(model), [model]);
  const isFluxPro1_1 = useMemo(
    () => getIsModelFluxPro1_1(model) || getIsModelFluxPro1_1Ultra(model),
    [model]
  );
  const isGptImage = useMemo(() => getIsModelGptImage(model), [model]);
  const isModelWithBase = useMemo(() => getIsModelWithBase(model), [model]);

  const dimensionLimits = useMemo(() => getDimensionLimits(model), [model]);
  const minWidth = useMemo(() => dimensionLimits.min, [dimensionLimits.min]);
  const maxWidth = useMemo(() => dimensionLimits.max, [dimensionLimits.max]);
  const minHeight = useMemo(() => dimensionLimits.min, [dimensionLimits.min]);
  const maxHeight = useMemo(() => dimensionLimits.max, [dimensionLimits.max]);

  const canHaveReference = useMemo(() => {
    if (!model || !model.capabilities?.length) {
      return true;
    }
    return model.capabilities.filter((type) => type !== "txt2img").length > 0;
  }, [model]);

  const referenceDimensions = useGetImageDimensions(
    referenceImages.reference?.url ?? ""
  );
  const controlDimensions = useGetImageDimensions(
    referenceImages.control?.url ?? ""
  );

  const referencesRatio = useMemo(() => {
    if (
      inferenceType === "controlnet_img2img" &&
      referenceImages.reference &&
      referenceImages.control
    ) {
      if (
        referenceDimensions.width &&
        referenceDimensions.height &&
        controlDimensions.width &&
        controlDimensions.height
      ) {
        return getImagesRatioDiff(referenceDimensions, controlDimensions);
      } else {
        return undefined;
      }
    } else {
      return 1;
    }
  }, [
    inferenceType,
    referenceDimensions,
    controlDimensions,
    referenceImages.reference,
    referenceImages.control,
  ]);
  const isReferencesRatioValid =
    referencesRatio === undefined || referencesRatio <= 1.2;

  // ----------------------------------

  const setReferenceControlNet = useCallback(
    (newReferenceControlNet: ControlNetPreset) => {
      _setReferenceControlNet(newReferenceControlNet);
      const isNewReferenceControlNetCanny =
        newReferenceControlNet === ControlNetPreset.CANNY;
      const isOldReferenceControlNetCanny =
        referenceControlNet === ControlNetPreset.CANNY;
      if (isNewReferenceControlNetCanny !== isOldReferenceControlNetCanny) {
        setControlEnd(isNewReferenceControlNetCanny ? 0.5 : 0.6);
      }
    },
    [_setReferenceControlNet, referenceControlNet]
  );

  const setModel = useCallback(
    (
      newModel: GetModelsByModelIdApiResponse["model"],
      options?: {
        avoidDimensionChange?: boolean;
      }
    ) => {
      setLastModelId(newModel.id);
      __setModel((currentModel) => {
        if (currentModel?.id === newModel.id) return currentModel;

        const isCustomDimensions = getIsCustomDimensions({
          model: currentModel,
          width,
          height,
        });

        let shouldUpdateDimensionsToBestRatio = false;
        if (!options?.avoidDimensionChange && !isCustomDimensions) {
          const newModelDimensions = getDimensionValues(newModel);
          const oldModelDimensions = getDimensionValues(currentModel);
          if (newModelDimensions !== oldModelDimensions) {
            shouldUpdateDimensionsToBestRatio = true;
          }
        }
        if (
          isCustomDimensions &&
          (getIsModelFluxPro(newModel) ||
            getIsModelFluxPro1_1(newModel) ||
            getIsModelFluxPro1_1Ultra(newModel) ||
            getIsModelGptImage(newModel))
        ) {
          shouldUpdateDimensionsToBestRatio = true;
        }

        if (shouldUpdateDimensionsToBestRatio) {
          const currentRatio = width / height;
          const closestDimension = getDimensionValues(newModel).reduce(
            (prev, curr) => {
              return Math.abs(curr.width / curr.height - currentRatio) <=
                Math.abs(prev.width / prev.height - currentRatio)
                ? curr
                : prev;
            }
          );
          _setWidth(closestDimension.width);
          _setHeight(closestDimension.height);
        }

        const allowedPresets = getAllowedControlNetPreset(newModel);
        if (
          !allowedPresets.nonComposable.includes(referenceControlNet) &&
          !allowedPresets.composable.includes(referenceControlNet)
        ) {
          setReferenceControlNet(ControlNetPreset.DEPTH);
        }

        const guidanceConfig = getGuidanceForModel({
          model: newModel,
          scheduler: scheduler,
        });
        _setGuidance(guidanceConfig.default);
        setGuidanceMax(guidanceConfig.max);
        setGuidanceMin(guidanceConfig.min);

        const samplingStepsConfig = getSamplingStepsForModel({
          model: newModel,
          scheduler: scheduler,
        });
        _setSamplingSteps(samplingStepsConfig.default);
        setSamplingStepsMin(samplingStepsConfig.min);
        setSamplingStepsMax(samplingStepsConfig.max);

        const maxNbImagesForModel = getMaxNbImagesForModel({ model: newModel });
        if (nbImages > maxNbImagesForModel) {
          __setNbImages(maxNbImagesForModel);
        }

        if (
          getIsModelSdxl(newModel) &&
          inferenceType === "controlnet_reference"
        ) {
          __setInferenceType("img2img");
          setReferenceInfluences(
            GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE["img2img"](
              newModel,
              isCanvasLiveMode
            )
          );
        } else if (
          newModel.capabilities?.length &&
          !newModel.capabilities.includes(inferenceType)
        ) {
          let newInferenceType =
            newModel.capabilities.filter((type) => type !== "txt2img")[0] ??
            "img2img";
          if (!isInferenceType(newInferenceType)) {
            newInferenceType = "img2img";
          }
          __setInferenceType(newInferenceType);
          setReferenceInfluences(
            GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE[newInferenceType](
              newModel,
              isCanvasLiveMode
            )
          );
          if (
            newInferenceType === "img2img" &&
            !newModel.capabilities.includes("img2img")
          ) {
            setReferenceImages({
              reference: undefined,
              control: undefined,
              ipAdapter: undefined,
            });
          }
        } else {
          setReferenceInfluences(
            GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE[inferenceType](
              newModel,
              isCanvasLiveMode
            )
          );
        }

        setConcepts(newModel.concepts);

        if (
          !getIsModelSdxl(newModel) &&
          baseModelId !== SDXL_DEFAULT_BASE_MODEL.id
        ) {
          _setBaseModelId(SDXL_DEFAULT_BASE_MODEL.id);
        }

        if (router.query.modelId !== newModel.id) {
          void router.replace({
            pathname: router.pathname,
            query: {
              ...router.query,
              modelId: newModel.id,
            },
          });
        }

        return newModel;
      });
    },
    [
      width,
      height,
      referenceControlNet,
      scheduler,
      isCanvasLiveMode,
      setLastModelId,
      nbImages,
      inferenceType,
      baseModelId,
      router,
      setReferenceControlNet,
    ]
  );

  const setModelFromModelId = useCallback(
    async (
      modelId: GetModelsByModelIdApiArg["modelId"],
      concepts?: GetModelsByModelIdApiResponse["model"]["concepts"]
    ) => {
      if (!modelId || model?.id === modelId) {
        return;
      }
      try {
        setIsLoadingModel(true);
        const info = await getModelById({
          modelId,
          projectId: selectedProject.id,
        }).unwrap();
        try {
          setModel(concepts ? { ...info.model, concepts } : info.model);
        } catch (err) {
          __setModel(undefined);
          setConcepts(undefined);
        }
      } finally {
        setIsLoadingModel(false);
      }
    },
    [setModel, model?.id, getModelById, selectedProject.id]
  );

  const setBaseModelId = useCallback((newBaseModelId: string) => {
    if (!SDXL_BASE_MODELS.find((model) => model.id === newBaseModelId)) {
      return;
    }
    _setBaseModelId(newBaseModelId as BaseModelId);
  }, []);

  useEffect(() => {
    if (
      model &&
      model.ownerId !== selectedProject.id &&
      model.privacy !== "public"
    ) {
      __setModel(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // ----------------------------------
  const resetReference = useCallback(() => {
    setReferenceImages({
      reference: undefined,
      control: undefined,
      ipAdapter: undefined,
    });
    setReferenceInfluences({
      reference: GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE.img2img(
        undefined,
        false
      ).reference,
      control: GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE.img2img(
        undefined,
        false
      ).control,
      ipAdapter: GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE.img2img(
        undefined,
        false
      ).ipAdapter,
    });
    __setInferenceType("img2img");
    _setReferenceControlNet(defaultControlNetPreset ?? ControlNetPreset.DEPTH);
    setMask(undefined);
    setReferenceModeMapAsset(undefined);
    setIsReferenceModeMapLoading(false);
  }, [defaultControlNetPreset]);

  const reset = useCallback(() => {
    if (router.query.modelId) {
      void setModelFromModelId(router.query.modelId as string);
    } else {
      __setModel(undefined);
      setConcepts(undefined);
    }
    __setNbImages(selectedTeam.isFreePlan ? 2 : 4);
    _setWidth(512);
    _setHeight(512);
    setSeed("");
    setPrompt("");
    setNegativePrompt("");
    __setScheduler(undefined);
    setControlEnd(0.6);

    resetReference();
  }, [
    router.query.modelId,
    selectedTeam.isFreePlan,
    setModelFromModelId,
    resetReference,
  ]);

  const getAsset = useCallback(
    async (assetId: string) => {
      try {
        const assetData = await getAssetById({
          assetId,
          projectId: selectedProject.id,
        }).unwrap();
        return assetData.asset;
      } catch (_) {}
    },
    [getAssetById, selectedProject.id]
  );

  const updateDimensionsToBestRatio = useCallback(
    async (asset: ReferenceAsset | undefined) => {
      if (
        isInCanvas ||
        isTexture ||
        !asset ||
        getIsCustomDimensions({
          model,
          width,
          height,
        })
      ) {
        return;
      }

      const { height: referenceImageHeight, width: referenceImageWidth } =
        asset.metadata.width && asset.metadata.height
          ? {
              height: asset.metadata.height,
              width: asset.metadata.width,
            }
          : await getImageDimensions(asset.url);

      const inputRatio = referenceImageWidth / referenceImageHeight;

      const bestRatio = getDimensionValues(model).reduce((prev, curr) => {
        return Math.abs(curr.width / curr.height - inputRatio) <=
          Math.abs(prev.width / prev.height - inputRatio)
          ? curr
          : prev;
      });
      const newWidth = bestRatio.width;
      const newHeight = bestRatio.height;

      if (newWidth !== width || newHeight !== height) {
        _setWidth(newWidth);
        _setHeight(newHeight);
        infoToast({
          title: "Aspect ratio has been adjusted",
        });
      }
    },
    [isInCanvas, isTexture, width, height, infoToast, model]
  );

  const setInferenceType = useCallback(
    (newInferenceType: typeof inferenceType) => {
      const oldInferenceType = inferenceType;

      if (oldInferenceType === newInferenceType) {
        return;
      }

      __setInferenceType(newInferenceType);

      setReferenceInfluences(
        GET_REFERENCE_INFLUENCE_BY_INFERENCE_TYPE[newInferenceType](
          model,
          isCanvasLiveMode
        )
      );

      if (
        IMAGE_FOR_DIMENSIONS_BY_INFERENCE_TYPE[oldInferenceType] !==
        IMAGE_FOR_DIMENSIONS_BY_INFERENCE_TYPE[newInferenceType]
      ) {
        void updateDimensionsToBestRatio(
          referenceImages[
            IMAGE_FOR_DIMENSIONS_BY_INFERENCE_TYPE[newInferenceType]
          ]
        );
      }
    },
    [
      inferenceType,
      model,
      isCanvasLiveMode,
      updateDimensionsToBestRatio,
      referenceImages,
    ]
  );

  const setReferenceImage = useCallback(
    (
      type: "reference" | "control" | "ipAdapter",
      asset: ReferenceAsset | undefined,
      options?: { preventDimensionsUpdate?: boolean }
    ) => {
      setReferenceImages((referenceImages) => {
        const newObject = {
          ...referenceImages,
          [type]: asset,
        };

        // Use lodash's isEqual for deep comparison
        if (!isEqual(referenceImages, newObject)) {
          return newObject;
        }

        // Return the original object if there's no change
        return referenceImages;
      });

      if (asset) {
        if (
          !options?.preventDimensionsUpdate &&
          type === IMAGE_FOR_DIMENSIONS_BY_INFERENCE_TYPE[inferenceType]
        ) {
          void updateDimensionsToBestRatio(asset);
        }

        toggleCollapsedSection("referenceImage", false);
      }
    },
    [toggleCollapsedSection, updateDimensionsToBestRatio, inferenceType]
  );

  const setReferenceImageFromAssetId = useCallback(
    async (
      type: "reference" | "control" | "ipAdapter",
      assetId: string,
      options?: { preventDimensionsUpdate?: boolean }
    ) => {
      try {
        setReferenceImages((referenceImages) => ({
          ...referenceImages,
          [type]: {
            isLoading: true,
          },
        }));

        const asset = await getAsset(assetId);
        if (!asset) throw new Error("NOT_FOUND");

        setReferenceImage(type, asset, options);
      } catch (err: any) {
        if (err.message === "NOT_FOUND") {
          setReferenceImage(type, undefined);
          return;
        }
        throw err;
      }
    },
    [setReferenceImage, getAsset]
  );

  const setReferenceInfluence = useCallback(
    (type: "reference" | "control" | "ipAdapter", strength: number) => {
      setReferenceInfluences((referenceInfluences) => ({
        ...referenceInfluences,
        [type]: strength,
      }));
    },
    []
  );

  const setReferenceFromInference = useCallback(
    async (
      inference:
        | GetModelsInferencesByModelIdAndInferenceIdApiResponse["inference"]
        | GetModelPresetsByModelIdApiResponse["presets"][0]
    ) => {
      resetReference();

      if (
        inference.parameters.type !== "txt2img" &&
        !inference.parameters.type.includes("inpaint")
      ) {
        setInferenceType(inference.parameters.type);
      }
      if (inference.parameters.ipAdapterType) {
        setIpAdapterType(inference.parameters.ipAdapterType);
      }

      let referenceParent = undefined;
      let isReferenceParentDetection = false;
      if (inference.parameters.imageId) {
        referenceParent = await getAsset(inference.parameters.imageId);
        if (
          referenceParent &&
          referenceParent.metadata.type === "detection" &&
          referenceParent.metadata.parentId &&
          inference.parameters.type === "controlnet"
        ) {
          referenceParent = await getAsset(referenceParent.metadata.parentId);
          isReferenceParentDetection = true;
        }
      }
      if (referenceParent) {
        await setReferenceImage("reference", referenceParent, {
          preventDimensionsUpdate: true,
        });
      }

      let controlParent = undefined;
      let isControlParentDetection = false;
      if (inference.parameters.controlImageId) {
        controlParent = await getAsset(inference.parameters.controlImageId);
        if (
          controlParent &&
          controlParent.metadata.type === "detection" &&
          controlParent.metadata.parentId
        ) {
          controlParent = await getAsset(controlParent.metadata.parentId);
          isControlParentDetection = true;
        }
      }
      if (controlParent) {
        await setReferenceImage("control", controlParent, {
          preventDimensionsUpdate: true,
        });
      }

      if (isReferenceParentDetection || isControlParentDetection) {
        setIsReferenceModeMappingEnabled(true);
      } else {
        setIsReferenceModeMappingEnabled(
          !inference.parameters.disableModalityDetection
        );
      }

      if (inference.parameters.modality) {
        const modality = getControlNetPresetFromString(
          inference.parameters.modality
        );
        const influence = getControlNetInfluenceFromString(
          inference.parameters.modality
        );
        if (modality) {
          setReferenceControlNet(modality.value);
          setReferenceInfluence(
            inference.parameters.strength === undefined
              ? "reference"
              : "control",
            influence
          );
        }
      }

      if (inference.parameters.type.includes("reference")) {
        setReferenceInfluence(
          "reference",
          inference.parameters.styleFidelity ?? 0
        );
      } else if (inference.parameters.strength) {
        setReferenceInfluence(
          "reference",
          reverseStrength(inference.parameters.strength ?? 0)
        );
      }

      const ipAdapterAssetId =
        inference.parameters.ipAdapterImageId ??
        inference.parameters.ipAdapterImageIds?.[0];
      if (ipAdapterAssetId) {
        const ipAdapterAsset = await getAsset(ipAdapterAssetId);
        if (ipAdapterAsset) {
          setReferenceImage(
            inference.parameters.type === "txt2img_ip_adapter"
              ? "reference"
              : "ipAdapter",
            ipAdapterAsset
          );
          if (
            inference.parameters.ipAdapterScale !== undefined ||
            inference.parameters.ipAdapterScales?.[0] !== undefined
          ) {
            const influence =
              inference.parameters.ipAdapterScale ??
              inference.parameters.ipAdapterScales?.[0];
            setReferenceInfluence(
              inference.parameters.type === "txt2img_ip_adapter"
                ? "reference"
                : "ipAdapter",
              influence!
            );
          }
        }
      }

      if (inference.parameters.type.includes("reference")) {
        if (inference.parameters.referenceAttn !== undefined) {
          setReferenceAttn(inference.parameters.referenceAttn);
        }
        if (inference.parameters.referenceAdain !== undefined) {
          setReferenceAdain(inference.parameters.referenceAdain);
        }
      }
    },
    [
      getAsset,
      resetReference,
      setInferenceType,
      setReferenceImage,
      setReferenceInfluence,
      setReferenceControlNet,
    ]
  );

  // ----------------------------------

  const setSamplingSteps = useCallback(
    (targetValue: number) => {
      const max = Math.min(
        samplingStepsMax,
        planSamplingStepsLimit ?? samplingStepsMax
      );
      const min = samplingStepsMin;
      const value = Math.max(Math.min(targetValue, max), min);
      _setSamplingSteps(value);
      if (targetValue !== value) {
        infoToast({
          title: "Sampling steps have been adjusted",
        });
      }
      return value;
    },
    [
      samplingStepsMin,
      samplingStepsMax,
      planSamplingStepsLimit,
      _setSamplingSteps,
      infoToast,
    ]
  );

  const setScheduler = useCallback(
    (newScheduler: SCHEDULER_TYPE | undefined) => {
      if (isFlux) {
        return;
      }

      const guidanceConfig = getGuidanceForModel({
        model: model,
        scheduler: newScheduler,
      });
      _setGuidance(guidanceConfig.default);
      setGuidanceMax(guidanceConfig.max);
      setGuidanceMin(guidanceConfig.min);

      const samplingStepsConfig = getSamplingStepsForModel({
        model: model,
        scheduler: newScheduler,
      });
      _setSamplingSteps(samplingStepsConfig.default);
      setSamplingStepsMin(samplingStepsConfig.min);
      setSamplingStepsMax(samplingStepsConfig.max);

      __setScheduler(newScheduler);
    },
    [isFlux, model]
  );

  const setWidth = useCallback(
    (targetWidth: number) => {
      const width = Math.min(
        Math.max(makeNumberAMultipleOfEight(targetWidth), dimensionLimits.min),
        dimensionLimits.max
      );
      _setWidth(width);
      if (targetWidth !== width && !isInCanvas) {
        infoToast({
          title: "Width has been adjusted",
        });
      }
      return width;
    },
    [dimensionLimits, infoToast, isInCanvas]
  );

  const setHeight = useCallback(
    (targetHeight: number) => {
      const height = Math.min(
        Math.max(makeNumberAMultipleOfEight(targetHeight), dimensionLimits.min),
        dimensionLimits.max
      );
      _setHeight(height);
      if (targetHeight !== height && !isInCanvas) {
        infoToast({
          title: "Height has been adjusted",
        });
      }
      return height;
    },
    [dimensionLimits, infoToast, isInCanvas]
  );

  const setGuidance = useCallback(
    (value: number) => {
      const newGuidance =
        _.max([
          _.min([value, guidanceMax ?? 999_999]),
          guidanceMin ?? -999_999,
        ]) ?? value;

      _setGuidance(newGuidance);

      if (newGuidance !== value) {
        infoToast({
          title: "Guidance has been adjusted",
        });
      }
    },
    [guidanceMax, guidanceMin, infoToast, _setGuidance]
  );

  // ----------------------------------

  const setNbImages = useCallback(
    (images: number) => {
      if (images > 0 && images <= maxBatchStandard) {
        __setNbImages(images);
      }
    },
    [maxBatchStandard]
  );

  const maxNbImages = useMemo(() => getMaxNbImagesForModel({ model }), [model]);

  // ----------------------------------

  const isAbleToGenerate =
    !referenceImages.reference?.isLoading &&
    !referenceImages.ipAdapter?.isLoading &&
    !referenceImages.control?.isLoading &&
    !isImportingAsset &&
    !isLoadingModel &&
    referencesRatio !== undefined;
  const isAbleToGenerateDebounced = useDebounce(isAbleToGenerate, 200, {
    preventFor: (value) => !value,
  });

  const inferenceContextValue = useMemo(
    () => ({
      isAbleToGenerate: isAbleToGenerateDebounced,
      isImportingAsset,
      setIsImportingAsset,
      model,
      isLoadingModel,
      setModel,
      baseModelId,
      setBaseModelId,
      setModelFromModelId,
      nbImages,
      setNbImages,
      maxNbImages,
      samplingSteps,
      setSamplingSteps,
      samplingStepsMin,
      setSamplingStepsMin,
      samplingStepsMax,
      setSamplingStepsMax,
      referenceImages,
      isReferencesRatioValid,
      setReferenceImage,
      setReferenceImageFromAssetId,
      width,
      minWidth,
      maxWidth,
      setWidth,
      height,
      minHeight,
      maxHeight,
      setHeight,
      guidance,
      setGuidance,
      guidanceMin,
      guidanceMax,
      seed,
      setSeed,
      referenceInfluences,
      setReferenceInfluence,
      inferenceType,
      setInferenceType,
      referenceControlNet,
      setReferenceControlNet,
      prompt,
      setPrompt,
      negativePrompt,
      setNegativePrompt,
      isReferenceModeMappingEnabled,
      setIsReferenceModeMappingEnabled,
      controlEnd,
      setControlEnd,
      referenceAttn,
      setReferenceAttn,
      referenceAdain,
      setReferenceAdain,
      ipAdapterType,
      setIpAdapterType,
      mask,
      setMask,
      scheduler,
      setScheduler,
      reset,
      isSdxl,
      isModelWithBase,
      isFlux,
      isFluxPro1_1,
      isGptImage,
      referenceModeMapAsset,
      setReferenceModeMapAsset,
      isReferenceModeMapLoading,
      setIsReferenceModeMapLoading,
      concepts,
      setConcepts,
      cuCost,
      setCuCost,
      isCuLoading,
      setIsCuLoading,
      isInCanvas,
      isTexture,
      canHaveReference,
      setIsCanvasLiveMode,
      isCanvasLiveMode,
      setReferenceFromInference,
    }),
    [
      isAbleToGenerateDebounced,
      isImportingAsset,
      setIsImportingAsset,
      model,
      isLoadingModel,
      setModel,
      baseModelId,
      setBaseModelId,
      setModelFromModelId,
      nbImages,
      setNbImages,
      maxNbImages,
      samplingSteps,
      setSamplingSteps,
      samplingStepsMin,
      setSamplingStepsMin,
      samplingStepsMax,
      setSamplingStepsMax,
      referenceImages,
      isReferencesRatioValid,
      setReferenceImage,
      setReferenceImageFromAssetId,
      width,
      minWidth,
      maxWidth,
      setWidth,
      height,
      minHeight,
      maxHeight,
      setHeight,
      guidance,
      setGuidance,
      guidanceMin,
      guidanceMax,
      seed,
      setSeed,
      referenceInfluences,
      setReferenceInfluence,
      inferenceType,
      setInferenceType,
      referenceControlNet,
      prompt,
      setPrompt,
      negativePrompt,
      setNegativePrompt,
      isReferenceModeMappingEnabled,
      setIsReferenceModeMappingEnabled,
      controlEnd,
      setControlEnd,
      mask,
      setMask,
      scheduler,
      setScheduler,
      reset,
      setReferenceControlNet,
      isSdxl,
      isModelWithBase,
      isFlux,
      isFluxPro1_1,
      isGptImage,
      referenceModeMapAsset,
      setReferenceModeMapAsset,
      isReferenceModeMapLoading,
      setIsReferenceModeMapLoading,
      referenceAttn,
      setReferenceAttn,
      referenceAdain,
      setReferenceAdain,
      ipAdapterType,
      setIpAdapterType,
      concepts,
      setConcepts,
      cuCost,
      setCuCost,
      isCuLoading,
      setIsCuLoading,
      isInCanvas,
      isTexture,
      canHaveReference,
      setIsCanvasLiveMode,
      isCanvasLiveMode,
      setReferenceFromInference,
    ]
  );

  return (
    <InferenceCreateContext.Provider value={inferenceContextValue}>
      {children}
    </InferenceCreateContext.Provider>
  );
}

export function useInferenceCreateContext() {
  return useContext<InferenceCreateContextValue>(InferenceCreateContext);
}
